import { 
  collection, 
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  orderBy,
  where,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';

// Create or update user in Firestore when they login/signup
export const syncUserToFirestore = async (authUser) => {
  try {
    const userRef = doc(db, 'users', authUser.uid);
    const userDoc = await getDoc(userRef);
    
    const username = authUser.email.split('@')[0];
    
    if (!userDoc.exists()) {
      // Create new user document
      await setDoc(userRef, {
        uid: authUser.uid,
        email: authUser.email,
        username: username,
        displayName: authUser.displayName || username,
        createdAt: serverTimestamp(),
        lastSeen: serverTimestamp(),
        isActive: true
      });
      console.log('userService: Created new user in Firestore:', authUser.email);
    } else {
      // Update existing user's last seen
      await updateDoc(userRef, {
        lastSeen: serverTimestamp(),
        isActive: true,
        // Update email in case it changed (rare but possible)
        email: authUser.email
      });
      console.log('userService: Updated user last seen:', authUser.email);
    }
    
    return {
      uid: authUser.uid,
      email: authUser.email,
      username: username,
      displayName: authUser.displayName || username
    };
  } catch (error) {
    console.error('userService: Error syncing user to Firestore:', error);
    throw error;
  }
};

// Get all users for mention autocomplete
export const getAllUsers = async () => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('isActive', '==', true), orderBy('username'));
    const querySnapshot = await getDocs(q);
    
    const users = [];
    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      users.push({
        uid: userData.uid,
        email: userData.email,
        username: userData.username,
        displayName: userData.displayName
      });
    });
    
    console.log('userService: Retrieved users for autocomplete:', users.length);
    return users;
  } catch (error) {
    console.error('userService: Error getting all users:', error);
    throw error;
  }
};

// Get user by username (for mention validation)
export const getUserByUsername = async (username) => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('username', '==', username), where('isActive', '==', true));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const userDoc = querySnapshot.docs[0];
    const userData = userDoc.data();
    
    return {
      uid: userData.uid,
      email: userData.email,
      username: userData.username,
      displayName: userData.displayName
    };
  } catch (error) {
    console.error('userService: Error getting user by username:', error);
    throw error;
  }
};

// Get user by email (alternative lookup)
export const getUserByEmail = async (email) => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email), where('isActive', '==', true));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const userDoc = querySnapshot.docs[0];
    const userData = userDoc.data();
    
    return {
      uid: userData.uid,
      email: userData.email,
      username: userData.username,
      displayName: userData.displayName
    };
  } catch (error) {
    console.error('userService: Error getting user by email:', error);
    throw error;
  }
};

// Update user profile (for future features)
export const updateUserProfile = async (uid, updates) => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    console.log('userService: Updated user profile:', uid);
  } catch (error) {
    console.error('userService: Error updating user profile:', error);
    throw error;
  }
};

// Mark user as inactive (soft delete)
export const deactivateUser = async (uid) => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      isActive: false,
      deactivatedAt: serverTimestamp()
    });
    console.log('userService: Deactivated user:', uid);
  } catch (error) {
    console.error('userService: Error deactivating user:', error);
    throw error;
  }
};